/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Azure Private Link", description = "Securely connect to Hightouch using Azure Private Link.", lastUpdatedDate = new Date(1744934400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Azure Private Link",
        description: "Securely connect to Hightouch using Azure Private Link.",
        lastUpdatedDate: "Fri Apr 18 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Azure Databricks",
          "depth": 0,
          "id": "azure-databricks"
        }, {
          "value": "Prerequisites",
          "depth": 1,
          "id": "prerequisites"
        }, {
          "value": "Getting started",
          "depth": 1,
          "id": "getting-started"
        }, {
          "value": "Azure Storage",
          "depth": 0,
          "id": "azure-storage"
        }, {
          "value": "Prerequisites",
          "depth": 1,
          "id": "prerequisites-1"
        }, {
          "value": "Getting started",
          "depth": 1,
          "id": "getting-started-1"
        }, {
          "value": "Learn more",
          "depth": 1,
          "id": "learn-more"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code"
    }, _provideComponents(), props.components), {BtOnly, PylonLink} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Private Link"
      }), "\n", _jsxs(_components.p, {
        children: ["Using ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/private-link/private-link-overview",
          children: "Private Link"
        }), ", you can securely connect your Azure-deployed services to Hightouch."]
      }), "\n", _jsx(_components.p, {
        children: "Network traffic between Hightouch and your services connected by Azure Private Link stays on the Microsoft backbone network and doesn't travel over the public internet. That means that you can restrict public access to your services, and your data is never exposed to the public internet."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch currently supports Azure Private Link for Azure Blob Storage and Azure Databricks."
      }), "\n", _jsx(_components.h2, {
        id: "azure-databricks",
        children: "Azure Databricks"
      }), "\n", _jsx(_components.h3, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch supports Azure Private Link on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier"
          }), " accounts."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Hightouch workspace must be in the Azure ", _jsx(_components.code, {
            children: "eastus"
          }), " Hightouch region."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["Please ", _jsx(PylonLink, {
          pylonText: "Hi, I'm interested in using Azure Private Link to connect Hightouch to my Azure Databricks workspace.",
          linkText: "reach out"
        }), " to our customer success team to get started with Azure Private Link for Azure Databricks."]
      }), "\n", _jsx(_components.h2, {
        id: "azure-storage",
        children: "Azure Storage"
      }), "\n", _jsx(_components.h3, {
        id: "prerequisites-1",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch supports Azure Private Link on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier"
          }), " accounts."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Hightouch workspace must be in the Azure ", _jsx(_components.code, {
            children: "eastus"
          }), " Hightouch region."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "getting-started-1",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["Please ", _jsx(PylonLink, {
          pylonText: "Hi, I'm interested in using Azure Private Link to connect Hightouch to my Azure Storage account.",
          linkText: "reach out"
        }), " to our customer success team to get started with Azure Private Link for Azure Storage."]
      }), "\n", _jsx(_components.h3, {
        id: "learn-more",
        children: "Learn more"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://learn.microsoft.com/en-us/azure/private-link/private-link-overview",
            children: "What is Azure Private Link?"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://learn.microsoft.com/en-us/azure/databricks/security/network/front-end/front-end-private-connect#private-connectivity-to-azure-databricks-overview",
            children: "Configure private connectivity to Azure Databricks"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://learn.microsoft.com/en-us/azure/storage/common/storage-private-endpoints",
            children: "Use private endpoints for Azure Storage"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
